import React, { useState } from "react";
import { FormField, FormItem, FormControl, FormMessage } from "@/components/ui/form";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { UseFormReturn } from "react-hook-form";
import { RegisterFormValues } from "@/types/auth";

interface TermsCheckboxProps {
  form: UseFormReturn<RegisterFormValues>;
}

export const TermsCheckbox = ({ form }: TermsCheckboxProps) => {
  const [showTerms, setShowTerms] = useState(false);
  const [showPrivacy, setShowPrivacy] = useState(false);

  return (
    <>
      <FormField
        control={form.control}
        name="terms_and_conditions_ok"
        render={({ field }) => (
          <FormItem className="flex flex-row items-start space-x-3 space-y-0 pt-2">
            <FormControl>
              <Checkbox
                checked={field.value}
                onCheckedChange={field.onChange}
                className="data-[state=checked]:bg-green-400"
              />
            </FormControl>
            <div className="space-y-1 leading-none">
              <span className="text-sm text-black">
                I accept both the{" "}
                <a
                  href="https://www.discoveryconciergeservices.com/terms-conditions"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-green-500 underline hover:text-green-600"
                >
                  Terms & Conditions
                </a>{" "}
                and{" "}
                <a
                  href="https://www.discoveryconciergeservices.com/privacy-policy"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-green-500 underline hover:text-green-600"
                >
                  Privacy Policy
                </a>
              </span>
              <FormMessage />
            </div>
          </FormItem>
        )}
      />

      <Dialog open={showTerms} onOpenChange={setShowTerms}>
        <DialogContent className="max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold text-black">Terms & Conditions</DialogTitle>
          </DialogHeader>
          <div className="text-black space-y-4 mt-4">
            <h2 className="text-lg font-semibold">Legal Policy for DISCO Party Bus Service</h2>
            
            <p className="text-sm">Effective Date: January 1, 2023</p>
            
            <p>
              Welcome to DISCO Party Bus Service ("DISCO," "we," "our," or "us"). By using our services, you agree to the 
              following terms and conditions. Please read this legal policy carefully.
            </p>
            
            <h3 className="text-md font-medium">1. Acceptance of Terms</h3>
            
            <p>
              By booking, using, or riding on our party buses, you acknowledge that you have read, understood, and agree to 
              be bound by this policy, as well as any additional terms communicated to you during the booking process. If you 
              do not agree, you must not use our services.
            </p>
            
            <h3 className="text-md font-medium">2. Eligibility</h3>
            <p>
              You represent and warrant that you are at least 18 years of age and are legally able to enter into this agreement. 
              If you are using the Service on behalf of an entity, you represent and warrant that you have the authority to bind 
              that entity to these Terms.
            </p>
            
            <h3 className="text-md font-medium">3. User Accounts</h3>
            <p>
              To use certain features of the Service, you must register for an account. You agree to provide accurate, current, 
              and complete information during the registration process and to update such information to keep it accurate, 
              current, and complete.
            </p>
            <p>
              You are responsible for safeguarding your account credentials and for all activities that occur under your account. 
              You agree to notify Disco immediately of any unauthorized use of your account or any other breach of security.
            </p>
            
            <h3 className="text-md font-medium">4. Driver Services</h3>
            <p>
              Drivers using the Disco platform agree to:
            </p>
            <ul className="list-disc pl-6 space-y-1">
              <li>Maintain all required licenses, permits, and insurance</li>
              <li>Comply with all applicable laws and regulations</li>
              <li>Maintain vehicles in safe and clean condition</li>
              <li>Provide professional and courteous service</li>
              <li>Follow Disco's policies and procedures</li>
            </ul>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};
